import queryString from "query-string";

import Context from "shared/util/Context";
import LocalEnvironment from "shared/util/LocalEnvironment";
import isRouteMatch from "shared/util/isRouteMatch";
import { isDesktop, isDev } from "shared/util/EnvironmentHelpers";
import { ROUTES } from "routers/shared/util/consts";

import {
	PREBID_VERSION,
	FORMATS,
	FORMATS_AOL,
	FORMATS_INDEX_EXCHANGE,
	PLACEMENTS_CRITEO,
	PLACEMENTS_OPENX,
	PLACEMENTS_AOL,
	PLACEMENTS_APPNEXUS,
	PLACEMENTS_INDEX_EXCHANGE,
	AMAZON_PUB_ID,
	PAGEMAP,
} from "./constants.js";

export function isDebugMode() {
	const queryObject = queryString.parse(window.location.search);
	const isDebug = "prebidDebug" in queryObject;

	return isDev || isDebug;
}

// external script loaders
// -----------------------------------------------------------------------------

function _loadScript(src) {
	const el = document.createElement("script");
	el.async = true;
	el.type = "text/javascript";
	el.src = src;
	const node = document.getElementsByTagName("script")[0];
	node.parentNode.insertBefore(el, node);
}

export function loadGPT() {
	_loadScript("https://www.googletagservices.com/tag/js/gpt.js");
}

export function loadPBJS() {
	const cdn = Context.get("cdnJS");
	const src = isDebugMode() ?
		`${LocalEnvironment.getEnvironmentUrl()}/flat/js/lib/prebid-${PREBID_VERSION}.js` :
		`${cdn}/flat/js/lib/prebid-${PREBID_VERSION}.min.js`;
	_loadScript(src);
}

export function loadAmazon() {
	// copied verbatim from their shit
	/* eslint-disable */
	!function(a9,a,p,s,t,A,g){if(a[a9])return;function q(c,r){a[a9]._Q.push([c,r])}a[a9]={init:function(){q("i",arguments)},fetchBids:function()
	{q("f",arguments)},setDisplayBids:function(){},_Q:[]};A=p.createElement(s);A.async=!0;A.src=t;g=p.getElementsByTagName(s)[0];g.parentNode.insertBefore( A,g)}("apstag",window,document,"script","//c.amazon-adsystem.com/aax2/apstag.js");
	/* eslint-enable */

	window.apstag.init({
		pubID: AMAZON_PUB_ID,
		adServer: "googletag",
	});
}

// setup networks
// -----------------------------------------------------------------------------

export function setupPBJS() {
	window.pbjs.que.push(() => {
		window.pbjs.setConfig({
			ix: {
				timeout: Context.get("prebidTimeout"),
			},
			enableSendAllBids: true,
		});

		window.pbjs.bidderSettings = {
			standard: {
				adserverTargeting: [
					{
						key: "hb_bidder",

						val(bidResponse) {
							return bidResponse.bidderCode;
						},
					},
					{
						key: "hb_adid",

						val(bidResponse) {
							return bidResponse.adId;
						},
					},
					{
						key: "hb_pb",

						val(bidResponse) {
							const { cpm } = bidResponse;
							let priceBucket;

							if (cpm < 5.00) {
								priceBucket = (Math.floor(cpm * 20) / 20).toFixed(2);
							} else if (cpm < 10.00) {
								priceBucket = (Math.floor(cpm * 10) / 10).toFixed(2);
							} else if (cpm < 20.00) {
								priceBucket = (Math.floor(cpm * 2) / 2).toFixed(2);
							} else {
								priceBucket = "20.00";
							}

							return priceBucket;
						},
					},
				],
			},
		};
	});
}

export function setupGPT() {
	window.googletag.cmd.push(() => {
		const ref = (document.referrer.indexOf("okcupid.com") !== -1) ?
			document.referrer : "//www.okcupid.com/home";

		window.googletag.pubads().set("page_url", ref);
		window.googletag.pubads().enableSingleRequest();
		window.googletag.pubads().enableAsyncRendering();
		window.googletag.enableServices();
	});
}


// get ad units
// -----------------------------------------------------------------------------

export function getSlotName(location = window.location) {
	const pageSlotMap = {
		"/home": "Home",
		"/logout": "Logout",
		"/mailbox": "Mailbox",
		"/messages": "Mailbox",
		"/match": "Match",
		"/help/a-list-extras": "Help-A-List-",
		"/feedback": "Feedback",
		"/doubletake": "Home",
		"/who-you-like": "Who-I-Rated",
		"/who-likes-you": "Who-I-Rated",
		"/settings": "Settings",
	};

	let slotName = "";

	if (pageSlotMap[location.pathname]) {
		slotName = pageSlotMap[location.pathname];
	}
	else if (isRouteMatch(location, ROUTES.ProfileQuestions)) {
		slotName = "Profile-Questions";
	}
	else if (isRouteMatch(location, ROUTES.ProfilePhotos)) {
		slotName = "Profile-Photos";
	}
	else if (isRouteMatch(location, ROUTES.ProfileMain)) {
		slotName = "Profile";
	}

	return slotName;
}

function _getPage() {
	return PAGEMAP[getSlotName()];
}

function _addAOL(bids, format) {
	const aolFormat = FORMATS_AOL[format];
	const page = _getPage();

	let placementId;

	// mobile/mobile_box have no pages
	if (PLACEMENTS_AOL[format]) {
		placementId = PLACEMENTS_AOL[format];
	}
	else if (page && PLACEMENTS_AOL[page] && PLACEMENTS_AOL[page][format]) {
		placementId = PLACEMENTS_AOL[page][format];
	}

	if (!placementId || !aolFormat) {
		return bids;
	}

	return bids.concat([{
		bidder: "aol",
		params: {
			placement: placementId,
			network: "9457.1",
			sizeId: aolFormat,
		},
	}]);
}

const _indexExchangePlacements = {};

function _addIndexExchange(bids, format, sizes) {
	const iePlacement = PLACEMENTS_INDEX_EXCHANGE[format];
	const ieFormat = FORMATS_INDEX_EXCHANGE[format];

	_indexExchangePlacements[format] = (_indexExchangePlacements[format] || 0) + 1;

	if (iePlacement && ieFormat) {
		sizes.forEach((size) => {
			bids.push({
				bidder: "ix",
				params: {
					id: ieFormat + _indexExchangePlacements[format],
					siteId: iePlacement,
					size,
				},
			});
		});

		return bids;
	} else {
		return bids;
	}
}

function _addAppNexus(bids, format) {
	const page = _getPage();
	let placementId;

	// mobile / mobile_box aren't paged
	if (PLACEMENTS_APPNEXUS[format]) {
		placementId = PLACEMENTS_APPNEXUS[format];
	}
	else if (page && PLACEMENTS_APPNEXUS[page] && PLACEMENTS_APPNEXUS[page][format]) {
		placementId = PLACEMENTS_APPNEXUS[page][format];
	}

	if (!placementId) {
		return bids;
	}

	return bids.concat([{
		bidder: "appnexus",
		params: {
			placementId,
			referrer: "okcupid.com",
		},
	}]);
}

function _addOpenX(bids, format) {
	const country = Context.get("country");
	const code = PLACEMENTS_OPENX[country] ?
		PLACEMENTS_OPENX[country][format] :
		PLACEMENTS_OPENX.OTHER[format];

	if (!Context.get("openXBidding") || !code) {
		return bids;
	}

	return bids.concat([{
		bidder: "openx",
		params: {
			unit: code,
			delDomain: "match-d.openx.net",
		},
	}]);
}

function _addCriteo(bids, format) {
	if (!Context.get("criteoBidding") || Context.get("country") !== "US") {
		return bids;
	}

	const code = PLACEMENTS_CRITEO[format];

	if (!code) {
		return bids;
	}

	return bids.concat([{
		bidder: "criteo",
		params: {
			zoneId: code,
		},
	}]);
}

export function getAdUnits(units) {
	const page = getSlotName();
	let slot;

	if (isDesktop) {
		slot = `/7214/mat.okcupid/${page}`;
	}
	else {
		const gender = Context.get("gender");
		const genderName = gender === 1 ? "m" : "f";
		slot = `/7214/OKCupid_Mobile/${genderName}`;
	}

	return units
		.filter(({ id, format }) => id && FORMATS[format])
		.map(({ id, format }) => {
			let sizes = FORMATS[format];
			const p = page;

			// more sizes on home
			if (p === "Home" && format === "leader") {
				sizes = FORMATS.leader_expanded;
			}

			let bids = [];

			bids = _addAppNexus(bids, format);
			bids = _addAOL(bids, format);
			bids = _addIndexExchange(bids, format, sizes);
			bids = _addCriteo(bids, format);
			bids = _addOpenX(bids, format);

			// return the appnexus bundle
			const bundle = {
				bids,
				slot,
				sizes,
				format,
				code: id,
			};

			return bundle;
		});
}

export function formatAdUnitsForPBJS(units) {
	if (!units || !units.length) {
		return units;
	}

	return units.map(({ sizes, ...ad }) => {
		return {
			mediaTypes: {
				banner: {
					sizes,
				},
			},
			...ad,
		};
	});
}



// WEBPACK FOOTER //
// ./src/shared/modules/onpageads/functions.js